import React, { useState, useMemo } from 'react'
import { useHRStore } from '../store/hrStore'
import { Award, Star, TrendingUp, Plus, Trash2 } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function PerformanceReviewsPage() {
  const { employees, performanceReviews, addPerformanceReview, getAveragePerformanceRating } = useHRStore()
  const [showAddModal, setShowAddModal] = useState(false)
  const [reviewForm, setReviewForm] = useState({
    employeeId: '',
    reviewer: '',
    rating: 3.5,
    feedback: '',
    goals: [],
  })
  const [goalInput, setGoalInput] = useState('')

  const avgRating = useMemo(() => getAveragePerformanceRating(), [getAveragePerformanceRating])

  // Get performance data by rating
  const ratingDistribution = useMemo(() => {
    const data = [
      { range: '5.0', count: employees.filter((e) => e.performanceRating >= 4.8).length },
      { range: '4.5', count: employees.filter((e) => e.performanceRating >= 4.3 && e.performanceRating < 4.8).length },
      { range: '4.0', count: employees.filter((e) => e.performanceRating >= 3.8 && e.performanceRating < 4.3).length },
      { range: '3.5', count: employees.filter((e) => e.performanceRating >= 3.3 && e.performanceRating < 3.8).length },
      { range: '3.0', count: employees.filter((e) => e.performanceRating < 3.3).length },
    ]
    return data
  }, [employees])

  const handleSaveReview = () => {
    if (!reviewForm.employeeId || !reviewForm.reviewer) {
      alert('Please fill in required fields')
      return
    }

    addPerformanceReview(reviewForm)
    setShowAddModal(false)
    setReviewForm({
      employeeId: '',
      reviewer: '',
      rating: 3.5,
      feedback: '',
      goals: [],
    })
    setGoalInput('')
  }

  const handleAddGoal = () => {
    if (goalInput.trim()) {
      setReviewForm({
        ...reviewForm,
        goals: [...reviewForm.goals, goalInput.trim()],
      })
      setGoalInput('')
    }
  }

  const handleRemoveGoal = (idx) => {
    setReviewForm({
      ...reviewForm,
      goals: reviewForm.goals.filter((_, i) => i !== idx),
    })
  }

  const getRecentReviews = (empId) => {
    return performanceReviews.filter((r) => r.employeeId === empId).slice(-1)
  }

  const getEmployeeName = (empId) => {
    return employees.find((e) => e.id === empId)?.name || 'Unknown'
  }

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-400'
    if (rating >= 4.0) return 'text-blue-400'
    if (rating >= 3.5) return 'text-yellow-400'
    return 'text-orange-400'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Performance Reviews</h1>
          <p className="text-slate-400">Conduct 360-degree reviews, set goals, and track employee development</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Average Rating</h3>
              <Star className="w-5 h-5 text-green-400" />
            </div>
            <div className={`text-3xl font-bold ${getRatingColor(parseFloat(avgRating))}`}>{avgRating}/5.0</div>
            <div className="text-sm text-slate-400">Organization average</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Reviews Completed</h3>
              <Award className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-white">{performanceReviews.length}</div>
            <div className="text-sm text-slate-400">This period</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6 hover:border-yellow-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">High Performers</h3>
              <TrendingUp className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-3xl font-bold text-white">{employees.filter((e) => e.performanceRating >= 4.5).length}</div>
            <div className="text-sm text-slate-400">Rating 4.5+</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Needs Improvement</h3>
              <Award className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white">{employees.filter((e) => e.performanceRating < 3.5).length}</div>
            <div className="text-sm text-slate-400">Rating below 3.5</div>
          </div>
        </div>

        {/* Performance Distribution */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Performance Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ratingDistribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="range" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }} />
              <Bar dataKey="count" fill="#0284c7" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Employee Performance */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Award className="w-6 h-6 text-purple-400" />
              Employee Performance
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              New Review
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Employee</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Department</th>
                  <th className="text-center py-3 px-4 text-blue-300 font-semibold">Rating</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Last Review</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  const reviews = getRecentReviews(emp.id)
                  return (
                    <tr key={emp.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                      <td className="py-3 px-4 text-white font-semibold">{emp.name}</td>
                      <td className="py-3 px-4 text-blue-300">{emp.department}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-lg font-bold ${getRatingColor(emp.performanceRating)}`}>
                          {emp.performanceRating}/5.0
                        </span>
                      </td>
                      <td className="py-3 px-4 text-cyan-400">
                        {reviews.length > 0 ? reviews[0].date : 'No reviews'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Review Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl border border-purple-500/20 max-w-md w-full mx-4 p-6 max-h-96 overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-4">New Performance Review</h2>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Employee</label>
                  <select
                    value={reviewForm.employeeId}
                    onChange={(e) => setReviewForm({ ...reviewForm, employeeId: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Reviewer</label>
                  <input
                    type="text"
                    value={reviewForm.reviewer}
                    onChange={(e) => setReviewForm({ ...reviewForm, reviewer: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Rating (1-5)</label>
                  <select
                    value={reviewForm.rating}
                    onChange={(e) => setReviewForm({ ...reviewForm, rating: parseFloat(e.target.value) })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="5.0">5.0 - Excellent</option>
                    <option value="4.5">4.5 - Very Good</option>
                    <option value="4.0">4.0 - Good</option>
                    <option value="3.5">3.5 - Satisfactory</option>
                    <option value="3.0">3.0 - Needs Improvement</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Feedback</label>
                  <textarea
                    value={reviewForm.feedback}
                    onChange={(e) => setReviewForm({ ...reviewForm, feedback: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="Review feedback"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Goals</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={goalInput}
                      onChange={(e) => setGoalInput(e.target.value)}
                      className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                      placeholder="Add goal"
                    />
                    <button
                      onClick={handleAddGoal}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-all"
                    >
                      Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {reviewForm.goals.map((goal, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-700/50 rounded p-2">
                        <span className="text-white text-sm">{goal}</span>
                        <button
                          onClick={() => handleRemoveGoal(idx)}
                          className="text-red-400 hover:text-red-300 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveReview}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Submit Review
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
